import type { LicenseStatus } from "@/hera-api/client/types.gen";
import {
  ALL_LICENSE_STATUSES,
  type AppLicenseStatus,
} from "@/lib/enum-constants";
import { getRegistrationStatusDisplay } from "@/lib/utils";
import type { StatusBadgeVariant } from "@/lib/utils";

/** Display entry for the registration status info panel. */
export interface RegistrationStatusDisplayItem {
  id: string;
  label: string;
  variant: StatusBadgeVariant;
}

/** All registration (license) statuses for the info panel. */
export function getRegistrationStatusDisplayList(): RegistrationStatusDisplayItem[] {
  return (ALL_LICENSE_STATUSES as readonly AppLicenseStatus[]).map((status) => {
    const { label, variant } = getRegistrationStatusDisplay(
      status,
      null,
      false,
    );
    return { id: status, label, variant };
  });
}

/**
 * Flow diagram: registration (license) status flow.
 * Each step is a LicenseStatus or a display-only step: "AWAITING_CHECK", "RENEWAL_DUE", "RENEWAL_OVERDUE".
 */
export type RegistrationFlowStepKey =
  | LicenseStatus
  | "CANCELLED_REQUIRES_PAYMENT"
  | "AWAITING_CHECK"
  | "RENEWAL_DUE"
  | "RENEWAL_OVERDUE";

/** One edge in the flow diagram: from status, what triggers it, to status. */
export interface RegistrationFlowEdge {
  from: RegistrationFlowStepKey;
  trigger: string;
  to: RegistrationFlowStepKey;
}

/**
 * All edges for the registration status flow diagram.
 * Main path only: payment -> review -> outcome. De-registration has a separate diagram.
 */
export const REGISTRATION_FLOW_EDGES: RegistrationFlowEdge[] = [
  { from: "REQUIRES_PAYMENT", trigger: "ACH", to: "PAYMENT_PROCESSING" },
  {
    from: "REQUIRES_PAYMENT",
    trigger: "Credit Card",
    to: "PAID_PENDING_REVIEW",
  },
  { from: "REQUIRES_PAYMENT", trigger: "Check", to: "AWAITING_CHECK" },
  {
    from: "REQUIRES_PAYMENT",
    trigger: "Pay later (cohort)",
    to: "PAY_LATER",
  },
  { from: "PAY_LATER", trigger: "ACH", to: "PAYMENT_PROCESSING" },
  {
    from: "PAY_LATER",
    trigger: "Credit Card",
    to: "PAID_PENDING_REVIEW",
  },
  { from: "PAY_LATER", trigger: "Check", to: "AWAITING_CHECK" },
  { from: "PAYMENT_PROCESSING", trigger: "", to: "PAID_PENDING_REVIEW" },
  // PAYMENT_FAILED is retired for new writes; failures return to REQUIRES_PAYMENT.
  { from: "PAYMENT_PROCESSING", trigger: "", to: "REQUIRES_PAYMENT" },
  { from: "AWAITING_CHECK", trigger: "", to: "PAID_PENDING_REVIEW" },
  {
    from: "PAID_PENDING_REVIEW",
    trigger: "",
    to: "COMPLIANT_UNTIL_RENEWAL_DATE",
  },
  { from: "PAID_PENDING_REVIEW", trigger: "", to: "REJECTED" },
  { from: "PAID_PENDING_REVIEW", trigger: "", to: "CANCELLED" },
  {
    from: "PAID_PENDING_REVIEW",
    trigger: "",
    to: "NON_COMPLIANT_NO_RENEWAL_POSSIBLE",
  },
  {
    from: "COMPLIANT_UNTIL_RENEWAL_DATE",
    trigger: "Service transfer (outstanding retained)",
    to: "CANCELLED_REQUIRES_PAYMENT",
  },
  {
    from: "CANCELLED_REQUIRES_PAYMENT",
    trigger: "Balance paid",
    to: "CANCELLED",
  },
  {
    from: "COMPLIANT_UNTIL_RENEWAL_DATE",
    trigger: "",
    to: "RENEWAL_DUE",
  },
  {
    from: "RENEWAL_DUE",
    trigger: "",
    to: "RENEWAL_OVERDUE",
  },
];

/** Order of nodes for the diagram. De-registration statuses have a separate diagram. */
export const REGISTRATION_FLOW_NODE_ORDER: RegistrationFlowStepKey[] = [
  "REQUIRES_PAYMENT",
  "PAY_LATER",
  "PAYMENT_PROCESSING",
  "PAYMENT_FAILED",
  "AWAITING_CHECK",
  "PAID_PENDING_REVIEW",
  "COMPLIANT_UNTIL_RENEWAL_DATE",
  "RENEWAL_DUE",
  "RENEWAL_OVERDUE",
  "NON_COMPLIANT_NO_RENEWAL_POSSIBLE",
  "REJECTED",
  "CANCELLED_REQUIRES_PAYMENT",
  "CANCELLED",
];

/** Grid position for flowchart: row and column (0-based). */
export interface RegistrationFlowNodePosition {
  row: number;
  col: number;
}

/**
 * Flowchart layout: left-to-right flow. (row, col) with row = vertical, col = horizontal.
 * Payment block matches inspection: col 0 = Balance Due, Payment Processing, Payment Failed;
 * col 1 = Awaiting Check.
 */
export const REGISTRATION_FLOW_LAYOUT: Record<
  RegistrationFlowStepKey,
  RegistrationFlowNodePosition
> = {
  REQUIRES_PAYMENT: { row: 1, col: 0 },
  PAY_LATER: { row: 3, col: 0 },
  PAYMENT_PROCESSING: { row: 4, col: 0 },
  PAYMENT_FAILED: { row: 6, col: 0 },
  AWAITING_CHECK: { row: 2, col: 1 },
  PAID_PENDING_REVIEW: { row: 1, col: 2 },
  COMPLIANT_UNTIL_RENEWAL_DATE: { row: 0, col: 3 },
  RENEWAL_DUE: { row: 0, col: 4 },
  RENEWAL_OVERDUE: { row: 1, col: 4 },
  NON_COMPLIANT_NO_RENEWAL_POSSIBLE: { row: 1, col: 3 },
  REJECTED: { row: 2, col: 3 },
  CANCELLED_REQUIRES_PAYMENT: { row: 4, col: 3 },
  CANCELLED: { row: 3, col: 3 },
  DEREG_PAYMENT_REQUIRED: { row: 2, col: 5 },
  DEREG_PAYMENT_PROCESSING: { row: 3, col: 5 },
  DE_REGISTRATION_PENDING: { row: 4, col: 5 },
  DEREG_PAYMENT_FAILED: { row: 5, col: 5 },
  EXEMPT: { row: 6, col: 5 },
};
